/* ==========================================================================
 * collection.monolith.js  —  by monolith
 * --------------------------------------------------------------------------
 * Client-side filter / sort / search engine for the custom collection page.
 *
 *  - Server renders page 1 of cards (Liquid, Locksmith-gated).
 *  - This script prefetches the WHOLE collection from products.json (250/page)
 *    immediately, builds matching cards in JS (markup mirrors the Liquid card),
 *    skips geo-blocked vendors, and appends them.
 *  - Every control mutates `state` and calls render(); render() re-runs on each
 *    prefetch batch too, so search/filters apply across the whole collection and
 *    results grow naturally while it streams in. "No results" is suppressed
 *    until loading finishes, so a partial set never looks empty.
 *
 * All behaviour is driven by the [data-cm-config] JSON blob emitted by the
 * section, so one asset serves every (independently configured) template.
 *
 * Tag-filter groups (config.tagGroups → per-gid meta):
 *   - selection "single": state.tagFilters[gid] is a string (one match value,
 *     '' = All); clicking is radio behaviour.
 *   - selection "multi":  state.tagFilters[gid] is an ARRAY of match values
 *     (OR — a product matching ANY is kept; [] = All). Clicking toggles a pill;
 *     "All" clears the group; aria-pressed reflects each pill independently.
 *   - dynamic true: after each render (once the WHOLE catalogue is prefetched)
 *     pills with no products under the OTHER active filters are hidden, and a
 *     stranded selection resets to "All". Computed via matchesExcept(node,gid),
 *     which runs every filter EXCEPT that group's own tag selection. Prefetch
 *     off ⇒ we only have page 1, so we DON'T fake it: warn once, leave pills.
 * The card markup below (buildCard) mirrors collection-card.monolith.liquid 1:1.
 * ========================================================================== */
(function () {
  'use strict';

  /* Products fetched from products.json, cached OUTSIDE the per-instance state so
   * they survive a re-init. The theme editor re-renders the section on every
   * settings change, which re-inits us — without this, each change refetched the
   * whole catalogue. Keyed by collection handle. */
  var PRODUCT_CACHE = {};
  var inDesignMode = !!(window.Shopify && window.Shopify.designMode);

  function initRoot(root) {
    if (!root || root.__cmInit) return;   // guard against double-init
    root.__cmInit = true;
    init(root);
  }
  function rootFrom(el) {
    if (!el) return null;
    if (el.matches && el.matches('[data-cm-root]')) return el;
    return el.querySelector ? el.querySelector('[data-cm-root]') : null;
  }
  function boot() {
    document.querySelectorAll('[data-cm-root]').forEach(initRoot);
  }

  function init(root) {
    /* Teardown registry. In the theme editor a section is re-rendered on every
     * settings change, so a fresh init() runs against new DOM while the old
     * instance's window listeners and MutationObserver keep firing against
     * detached nodes. Anything global gets registered here and released by the
     * shopify:section:unload handler at the bottom. (Editor-only: a storefront
     * page load only ever inits once.) */
    var teardown = [];
    /* Every async loop in this instance checks `aborted`. Without it the theme
     * editor re-renders the section on EVERY settings change (including the
     * mobile-preview switch), which re-inits us while the previous instance's
     * full-catalogue prefetch is still running — it keeps building hundreds of
     * cards into a grid that is no longer in the document, and the prefetches
     * pile up until the editor tab dies. */
    var aborted = false;
    root.__cmTeardown = function () {
      aborted = true;
      teardown.forEach(function (fn) { try { fn(); } catch (e) {} });
      teardown.length = 0;
      root.__cmInit = false;
    };

    var config = {};
    try { config = JSON.parse(root.querySelector('[data-cm-config]').textContent); } catch (e) { config = {}; }

    var grid       = root.querySelector('[data-cm-grid]');
    var searchEl   = root.querySelector('[data-cm-search]');
    var sortEl     = root.querySelector('[data-cm-sort]');
    var countEl    = root.querySelector('[data-cm-count]');
    var noResults  = root.querySelector('[data-cm-noresults]');
    var pagination = root.querySelector('[data-cm-pagination]');
    var loadingEl  = root.querySelector('[data-cm-loading]');
    var loadedEl   = root.querySelector('[data-cm-loaded]');
    var totalEl    = root.querySelector('[data-cm-total]');
    if (!grid) return;

    function log(msg) { try { console.log('[collection.monolith] ' + msg); } catch (e) {} }

    /* Locksmith-blocked vendors, normalised. These are the ONLY product-level
     * locks on this shop (verified against locksmith-variables: every
     * product-affecting condition is product.vendor == one of three vendors;
     * the remaining locks are page/blog/article/link/cart scoped). products.json
     * honours no Locksmith rule, so this vendor filter IS the gate for every
     * prefetched / portal card — an exact case-sensitive compare would silently
     * leak a locked vendor whose casing or spacing differs from the setting. */
    var blockedVendors = (config.blockedVendors || [])
      .map(function (v) { return String(v).trim().toLowerCase(); }).filter(Boolean);
    function vendorBlocked(vendor) {
      return blockedVendors.indexOf(String(vendor == null ? '' : vendor).trim().toLowerCase()) !== -1;
    }
    var preorderTags   = (config.preorderTags || '').toLowerCase().split(',')
                           .map(function (t) { return t.trim(); }).filter(Boolean);
    var newSeconds     = config.newSeconds || 0;

    // Real prefetch requires both the toggle AND a handle to fetch from.
    var prefetchOn     = !!(config.prefetch && config.handle);

    // Add-to-cart copy (mirrors the Liquid card); blank mobile text falls back.
    var atcText = config.atcText || 'Add to cart';
    var atcTextMobile = (config.atcTextMobile || '').trim() || atcText;

    // Vendors pushed to the end of Featured / New arrivals (lowercased set).
    var demoteVendors = (config.demoteVendors || []).map(function (v) { return String(v).trim().toLowerCase(); }).filter(Boolean);

    var state = {
      search: '',
      brand: '__all__',
      stock: 'all',
      discount: null,          // null = All; otherwise a bucket label (e.g. 40)
      sort: config.defaultSort || 'featured',
      tagFilters: {},          // gid  -> string (single) | array (multi)
      custom: {},              // group key -> pill gid (single) | array (multi)
      portal: null             // active portal pid, or null for the real collection
    };

    /* ---------- custom filter pills (block-defined rules) ----------
     * config.customPills is a flat, block-ordered list; pills sharing `group`
     * form one row and the FIRST pill of a row carries the row's options
     * (selection mode, dynamic, resetting). A pill matches a product when every
     * rule it sets passes (collection AND vendor AND tags), or when the product
     * id is explicitly listed. Collection rules resolve to an id set fetched
     * from that collection's products.json — Liquid would cap us at 50. */
    var customPills = {};        // gid -> rule
    var customGroups = {};       // groupKey -> { selection, dynamic, resetting, pills: [gid] }
    (config.customPills || []).forEach(function (p) {
      var key = String(p.group || 'custom').toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      var rule = {
        gid: p.gid,
        group: key,
        collection: (p.collection || '').trim(),
        collectionIds: null,                    // filled in lazily by fetch
        vendor: (p.vendor || '').trim().toLowerCase(),
        tags: String(p.tags || '').split(',').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean),
        ids: String(p.ids || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean)
      };
      customPills[p.gid] = rule;
      if (!customGroups[key]) {
        customGroups[key] = {
          selection: p.selection === 'multi' ? 'multi' : 'single',
          dynamic: !!p.dynamic,
          resetting: !!p.resetting,
          // "other" turns the row's reset pill into a real NEGATIVE filter
          resetMode: p.resetMode === 'other' ? 'other' : 'all',
          pills: []
        };
        state.custom[key] = p.selection === 'multi' ? [] : '';
      }
      customGroups[key].pills.push(p.gid);
    });

    var portals = {};
    (config.portals || []).forEach(function (p) { portals[p.pid] = p; });

    // Per-group behaviour, read from config.tagGroups. Seeds tagFilters with the
    // right empty shape so matches()/clearAll() never has to guess single vs multi.
    var tagGroupMeta = {};
    (config.tagGroups || []).forEach(function (g) {
      var gid = String(g.id);
      var multi = g.selection === 'multi';
      tagGroupMeta[gid] = {
        selection: multi ? 'multi' : 'single',
        dynamic: !!g.dynamic,
        resetting: !!g.resetting,          // always visible + clears the other groups
        featured: (g.featured || '').toLowerCase()
      };
      state.tagFilters[gid] = multi ? [] : '';
    });
    var brandGroup = config.brandGroup || {};
    var discountGroup = config.discountGroup || {};
    var dynamicWarned = {};   // gid -> true once we've warned that prefetch is off

    var items = [];           // cached card nodes (in DOM/insertion order)
    var seenIds = {};         // product ids already in the DOM (dedupe)
    var loading = false;
    var catalogueComplete = false;  // true once the WHOLE catalogue is loaded (gates dynamic pills)

    /* ---------- data access (memoised per node) ---------- */
    function data(node) {
      if (node._cm) return node._cm;
      var ds = node.dataset;
      var d = {
        id: String(ds.productId || ''),
        portalId: ds.cmPortalItem || '',   // '' = belongs to the real collection
        brand: ds.brand || '',
        brandLC: (ds.brand || '').toLowerCase(),
        name: (ds.name || '').toLowerCase(),
        title: (ds.title || '').toLowerCase(),
        stock: ds.stock || '',
        isNew: ds.isNew === 'true',
        price: parseFloat(ds.price || '0') || 0,
        discount: parseInt(ds.discount || '0', 10) || 0,
        created: parseInt(ds.created || '0', 10) || 0,
        tags: (ds.tags || '').toLowerCase().split('|')
      };
      node._cm = d;
      return d;
    }

    /* Insertion order is stamped ONCE per card and never re-derived from the
     * DOM. Sorting now physically reorders the grid (for correct tab / screen-
     * reader order) and refreshCache() re-reads the DOM after every prefetch
     * batch — so without this stamp, sorting by price and then receiving one
     * more batch would hand `items` back in PRICE order, and "Featured" (which
     * IS insertion order) would silently inherit it and never return to the
     * merchant's curated order. */
    var seqCounter = 0;
    function refreshCache() {
      items = Array.prototype.slice.call(grid.querySelectorAll('[data-cm-item]'));
      for (var i = 0; i < items.length; i++) {
        if (items[i]._cmSeq == null) items[i]._cmSeq = seqCounter++;
      }
      items.sort(function (a, b) { return a._cmSeq - b._cmSeq; });
    }

    /* ---------- filtering ---------- */
    // skipTagGid (optional): ignore that group's OWN tag selection — used by the
    // dynamic-pill pass so a group can still see products it would itself hide.
    /* Does one custom pill's rule accept this product?
     * Explicit ids always win. Otherwise every rule the merchant SET must pass
     * (collection AND vendor AND tags); tags are OR within themselves. A pill
     * with no rules at all matches nothing — silently matching everything would
     * make a mis-configured pill look like it worked. */
    function customPillMatches(rule, d) {
      if (rule.ids.length && rule.ids.indexOf(d.id) !== -1) return true;
      var hasRule = false;
      if (rule.collection) {
        hasRule = true;
        if (!rule.collectionIds || !rule.collectionIds[d.id]) return false;
      }
      if (rule.vendor) {
        hasRule = true;
        if (d.brandLC !== rule.vendor) return false;
      }
      if (rule.tags.length) {
        hasRule = true;
        var hit = false;
        for (var i = 0; i < rule.tags.length; i++) { if (d.tags.indexOf(rule.tags[i]) !== -1) { hit = true; break; } }
        if (!hit) return false;
      }
      return hasRule;
    }

    // skipCustomKey mirrors skipTagGid for the dynamic pass on custom rows.
    function matchesCustom(d, skipCustomKey) {
      for (var key in state.custom) {
        if (key === skipCustomKey) continue;
        var sel = state.custom[key];
        var meta = customGroups[key] || {};
        var hasSel = Array.isArray(sel) ? sel.length > 0 : !!sel;
        if (!hasSel) {
          /* Nothing picked in this row. With the default "All" reset that means
           * no constraint. With "Other" the reset pill is itself a filter: keep
           * only products that match NONE of this row's pills. Because it goes
           * through the normal matcher, every other row's dynamic pass narrows
           * to the Other set automatically. */
          if (meta.resetMode === 'other') {
            var own = meta.pills || [];
            for (var oi = 0; oi < own.length; oi++) {
              var orule = customPills[own[oi]];
              if (orule && customPillMatches(orule, d)) return false;
            }
          }
          continue;
        }
        var list = Array.isArray(sel) ? sel : [sel];
        var any = false;
        for (var i = 0; i < list.length; i++) {
          var rule = customPills[list[i]];
          if (rule && customPillMatches(rule, d)) { any = true; break; }
        }
        if (!any) return false;
      }
      return true;
    }

    function matches(node, skipTagGid, skipCustomKey) {
      var d = data(node);
      // Portal gate first: portal products NEVER show on the default view, and
      // while a portal owns the grid nothing else shows. Everything below then
      // filters within whichever set is on screen.
      if (state.portal) { if (d.portalId !== state.portal) return false; }
      else if (d.portalId) return false;
      if (!matchesCustom(d, skipCustomKey)) return false;
      if (state.brand !== '__all__' && d.brand !== state.brand) return false;
      if (state.stock !== 'all') {
        if (state.stock === 'new') { if (!d.isNew) return false; }
        else if (d.stock !== state.stock) return false;
      }
      if (state.discount != null) {
        if (d.discount <= 0) return false;                                   // not on sale
        var b = bucketFor(state.discount);
        if (b) { if (d.discount < b.min || d.discount > b.max) return false; }
        else if (Math.abs(d.discount - state.discount) > DISCOUNT_TOL) return false;
      }
      if (state.search) {
        var q = state.search;
        if (d.name.indexOf(q) === -1 && d.title.indexOf(q) === -1 && d.brandLC.indexOf(q) === -1) return false;
      }
      for (var gid in state.tagFilters) {
        if (gid === skipTagGid) continue;
        var sel = state.tagFilters[gid];
        if (sel == null) continue;
        if (typeof sel === 'string') {
          if (sel && d.tags.indexOf(sel) === -1) return false;              // single: must contain the one value
        } else if (sel.length) {                                            // multi: OR — must contain at least one
          var hit = false;
          for (var si = 0; si < sel.length; si++) { if (d.tags.indexOf(sel[si]) !== -1) { hit = true; break; } }
          if (!hit) return false;
        }
      }
      return true;
    }
    // Every filter EXCEPT skipGid's own tag selection (standard facet behaviour).
    function matchesExcept(node, skipGid) { return matches(node, skipGid); }

    /* ---------- sorting ---------- */
    // Brands listed in "Push these brands to the end" are demoted under the two
    // recency-ish sorts only (Featured / New arrivals), where a vendor with
    // constant new releases would otherwise bury everything else. Price and A–Z
    // are objective orders, so demotion there would just look broken. The split
    // is stable, so relative order inside each half is preserved.
    function isDemoted(d) { return demoteVendors.length > 0 && demoteVendors.indexOf(d.brandLC) !== -1; }
    function applyDemotion(arr) {
      if (!demoteVendors.length) return arr;
      var keep = [], push = [];
      for (var i = 0; i < arr.length; i++) (isDemoted(data(arr[i])) ? push : keep).push(arr[i]);
      if (!push.length) return arr;
      arr.length = 0;
      Array.prototype.push.apply(arr, keep.concat(push));
      return arr;
    }

    function sortNodes(arr) {
      var s = state.sort;
      // Featured sorts on the stamped sequence, not on however `arr` happens to
      // be ordered right now — see refreshCache().
      if (s === 'featured') {
        arr.sort(function (a, b) { return (a._cmSeq || 0) - (b._cmSeq || 0); });
        return applyDemotion(arr);
      }
      arr.sort(function (a, b) {
        var da = data(a), db = data(b);
        if (s === 'az') return da.title.localeCompare(db.title);
        if (s === 'price-asc') return da.price - db.price;
        if (s === 'price-desc') return db.price - da.price;
        if (s === 'new') return db.created - da.created;
        return 0;
      });
      if (s === 'new') applyDemotion(arr);
      return arr;
    }

    /* ---------- render (diff-based: only touch changed nodes) ---------- */
    var lastOrderKey = null;      // guards the DOM reorder (see below)
    function render() {
      if (aborted) return;
      var matched = [];
      for (var i = 0; i < items.length; i++) {
        if (matches(items[i])) matched.push(items[i]);
      }
      sortNodes(matched);

      var inSet = typeof Set !== 'undefined' ? new Set(matched) : null;
      var has = inSet ? function (n) { return inSet.has(n); } : function (n) { return matched.indexOf(n) !== -1; };

      for (var j = 0; j < items.length; j++) {
        var node = items[j];
        var show = has(node);
        var shown = node.style.display !== 'none';
        if (show !== shown) node.style.display = show ? '' : 'none';
      }
      /* Sort order is applied to the DOM, not via CSS `order`. Visual-only
       * reordering leaves tab order and screen-reader order in the original
       * sequence (WCAG 1.3.2 Meaningful Sequence / 2.4.3 Focus Order), so a
       * keyboard or AT user sorted by price walks the grid in insertion order.
       * Reordering ~2k nodes on every keystroke would be wasteful, so we only
       * re-append when the sort actually changed or new cards streamed in. */
      var orderKey = state.sort + '|' + (state.portal || '') + '|' + items.length;
      if (orderKey !== lastOrderKey) {
        lastOrderKey = orderKey;
        var frag = document.createDocumentFragment();
        for (var k = 0; k < matched.length; k++) frag.appendChild(matched[k]);
        grid.appendChild(frag);
      }

      if (countEl) countEl.textContent = matched.length + (matched.length === 1 ? ' product' : ' products');
      if (noResults) noResults.hidden = !(matched.length === 0 && !loading);

      applyDynamicPills();   // narrow dynamic groups to what's still reachable (may re-render)

      paintRowChrome();         // drop rows/headings/dividers left with nothing
      paintClearAffordances();  // per-row Clear + sidebar Clear all follow the state
      syncDropdowns();          // dropdown rows mirror their (possibly narrowed) pills
      writeUrl();               // mirror the active filter state into the URL (shareable link)
    }

    /* ---------- discount buckets ------------------------------------------
     * Consolidates near-identical discounts into round click-boxes.
     *
     *   1. Collect every distinct discount % currently loaded.
     *   2. Cluster values sitting within ±tol of a common centre (span 2*tol),
     *      so 39% and 42% share one box.
     *   3. Label = that centre snapped to the nearest LABEL_SNAP. Real catalogue
     *      discounts run near-continuous (20,23,24,28,30,31...), so raw centres
     *      read as -22% / -31% / -38%; snapping gives -20% / -30% / -40%.
     *
     * Clicking filters on the cluster's OWN range, not on ±tol around the label:
     * the label is a rounded display value, so measuring against it would strand
     * members of its own cluster (a 24% product in a "-20%" box).
     *
     * Buckets are data-driven, so they're rebuilt as prefetch streams in.
     * -------------------------------------------------------------------- */
    var DISCOUNT_TOL = 2;                              // half the width of a cluster
    var LABEL_SNAP = 5;                                // labels always read as a multiple of this
    var discountBuckets = [];                          // [{label, min, max}]

    /* Is this card part of whatever set is on screen right now? Portal cards are
     * never removed from the DOM, so anything that reasons about "the current
     * products" (pill pruning, discount buckets) has to apply the same gate the
     * render path does — otherwise a visited portal permanently pollutes the
     * normal collection's facets. */
    function inScope(node) {
      var pidOf = data(node).portalId;
      return state.portal ? pidOf === state.portal : !pidOf;
    }

    function computeDiscountBuckets() {
      var seen = {};
      for (var i = 0; i < items.length; i++) {
        if (!inScope(items[i])) continue;
        var v = data(items[i]).discount;
        if (v > 0) seen[v] = true;
      }
      var vals = Object.keys(seen).map(Number).sort(function (a, b) { return a - b; });
      if (!vals.length) return [];

      var raw = [], min = vals[0], max = vals[0];
      for (var j = 1; j < vals.length; j++) {
        if (vals[j] - min <= DISCOUNT_TOL * 2) { max = vals[j]; continue; }
        raw.push({ min: min, max: max });
        min = max = vals[j];
      }
      raw.push({ min: min, max: max });

      var out = [];
      for (var k = 0; k < raw.length; k++) {
        var c = raw[k];
        var label = Math.round((c.min + c.max) / 2 / LABEL_SNAP) * LABEL_SNAP;
        var prev = out.length ? out[out.length - 1] : null;
        if (prev && prev.label === label) { prev.max = c.max; continue; }  // neighbours snapped alike
        out.push({ label: label, min: c.min, max: c.max });
      }
      return out;
    }
    function bucketFor(label) {
      for (var i = 0; i < discountBuckets.length; i++) {
        if (discountBuckets[i].label === label) return discountBuckets[i];
      }
      return null;
    }
    function rebuildDiscountPills() {
      var row = root.querySelector('[data-cm-discount-row]');
      if (!row) return;                                // block absent or switched off
      var wrap = row.querySelector('[data-cm-discount-pills]');
      if (!wrap) return;

      discountBuckets = computeDiscountBuckets();
      if (!discountBuckets.length) { row.hidden = true; return; }   // nothing on sale
      row.hidden = false;

      /* #7: the shopper's choice OUTRANKS a shifting cluster. Buckets are
       * recomputed on every prefetch page, and the label of a cluster can move
       * as more products stream in. Nulling the selection there silently
       * reverted "-40%" to All mid-browse (and broke a shared ?disc= link whose
       * bucket only appears on a later page). Instead, keep the selection and
       * synthesise its bucket if it isn't in the current set — matches() falls
       * back to a +/- tolerance compare, so it still filters sensibly, and the
       * bucket reappears intact once its products arrive. */
      var sel = state.discount;
      if (sel != null && !bucketFor(sel)) {
        discountBuckets = discountBuckets.concat([{ label: sel, min: sel - DISCOUNT_TOL, max: sel + DISCOUNT_TOL }])
          .sort(function (a, b) { return a.label - b.label; });
      }

      var html = '<button type="button" class="collection-monolith__pill' + (sel == null ? ' is-active' : '') +
                 '" data-cm-discount data-value="__all__" aria-pressed="' + (sel == null ? 'true' : 'false') + '">All</button>';
      for (var i = 0; i < discountBuckets.length; i++) {
        var lab = discountBuckets[i].label, on = sel === lab;
        html += '<button type="button" class="collection-monolith__pill' + (on ? ' is-active' : '') +
                '" data-cm-discount data-value="' + lab + '" aria-pressed="' + (on ? 'true' : 'false') + '">-' + lab + '%</button>';
      }
      wrap.innerHTML = html;                           // click handling is delegated, so this is safe
    }

    /* ---------- pill helpers ---------- */
    function setActive(pills, activeEl) {
      for (var i = 0; i < pills.length; i++) {
        var on = pills[i] === activeEl;
        pills[i].classList.toggle('is-active', on);
        pills[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }
    // Multi-select group: each pill reflects its own selected state; "All" (empty
    // match) is active exactly when nothing is selected.
    function updateMultiPills(pills, gid) {
      var arr = state.tagFilters[gid];
      var empty = !(arr && arr.length);
      for (var i = 0; i < pills.length; i++) {
        var m = pills[i].getAttribute('data-cm-match') || '';
        var on = m === '' ? empty : (arr && arr.indexOf(m) !== -1);
        pills[i].classList.toggle('is-active', !!on);
        pills[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }

    /* ---------- resetting groups ----------
     * A "resetting" row is a top-level switch: it never gets narrowed by the
     * dynamic pass, and choosing one of its pills wipes every OTHER filter
     * group so the shopper lands on a clean view of just that choice. `skip`
     * names the group doing the resetting so we don't clear the click itself. */
    function resetOtherGroups(skip) {
      if (skip !== 'brand') {
        state.brand = '__all__';
        reset(root.querySelectorAll('[data-cm-brand]'), '__all__', 'data-value');
      }
      state.stock = 'all';
      reset(root.querySelectorAll('[data-cm-stock]'), 'all', 'data-value');
      if (skip !== 'discount') {
        state.discount = null;
        reset(root.querySelectorAll('[data-cm-discount]'), '__all__', 'data-value');
      }
      Object.keys(state.tagFilters).forEach(function (g) {
        if (skip === 'tag:' + g) return;
        var pills = root.querySelectorAll('[data-cm-tag="' + g + '"]');
        if ((tagGroupMeta[g] || {}).selection === 'multi') { state.tagFilters[g] = []; updateMultiPills(pills, g); }
        else { state.tagFilters[g] = ''; reset(pills, '', 'data-cm-match'); }
      });
      Object.keys(state.custom).forEach(function (k) {
        if (skip === 'custom:' + k) return;
        var pills = root.querySelectorAll('[data-cm-custom="' + k + '"]');
        if ((customGroups[k] || {}).selection === 'multi') { state.custom[k] = []; updateCustomMultiPills(pills, k); }
        else { state.custom[k] = ''; reset(pills, '', 'data-cm-value'); }
      });
    }

    function updateCustomMultiPills(pills, key) {
      var arr = state.custom[key] || [];
      for (var i = 0; i < pills.length; i++) {
        var v = pills[i].getAttribute('data-cm-value') || '';
        var on = v === '' ? arr.length === 0 : arr.indexOf(v) !== -1;
        pills[i].classList.toggle('is-active', !!on);
        pills[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }

    /* ---------- control bindings (delegated; pills survive rebuilds) ---------- */
    root.addEventListener('click', function (e) {
      var brandPill = e.target.closest('[data-cm-brand]');
      if (brandPill) {
        var bval = brandPill.getAttribute('data-value');
        if (brandGroup.resetting && bval !== '__all__') resetOtherGroups('brand');
        setActive(root.querySelectorAll('[data-cm-brand]'), brandPill);
        state.brand = bval;
        render(); return;
      }
      var customPill = e.target.closest('[data-cm-custom]');
      if (customPill) {
        var ckey = customPill.getAttribute('data-cm-custom');
        var cval = customPill.getAttribute('data-cm-value') || '';
        var cgroup = root.querySelectorAll('[data-cm-custom="' + ckey + '"]');
        var cmeta = customGroups[ckey] || { selection: 'single' };
        if (cmeta.resetting && cval !== '') resetOtherGroups('custom:' + ckey);
        if (cmeta.selection === 'multi') {
          if (cval === '') {
            state.custom[ckey] = [];                       // the row's "All"
          } else {
            var carr = state.custom[ckey];
            if (!Array.isArray(carr)) carr = state.custom[ckey] = [];
            var cidx = carr.indexOf(cval);
            if (cidx === -1) carr.push(cval); else carr.splice(cidx, 1);
          }
          updateCustomMultiPills(cgroup, ckey);
        } else {
          setActive(cgroup, customPill);
          state.custom[ckey] = cval;
        }
        render(); return;
      }
      var stockPill = e.target.closest('[data-cm-stock]');
      if (stockPill) {
        setActive(root.querySelectorAll('[data-cm-stock]'), stockPill);
        state.stock = stockPill.getAttribute('data-value');
        render(); return;
      }
      var discPill = e.target.closest('[data-cm-discount]');
      if (discPill) {
        var dvRaw = discPill.getAttribute('data-value');
        if (discountGroup.resetting && dvRaw !== '__all__') resetOtherGroups('discount');
        setActive(root.querySelectorAll('[data-cm-discount]'), discPill);
        var dv = dvRaw;
        state.discount = dv === '__all__' ? null : parseInt(dv, 10);
        render(); return;
      }
      var tagPill = e.target.closest('[data-cm-tag]');
      if (tagPill) {
        var gid = tagPill.getAttribute('data-cm-tag');
        var group = root.querySelectorAll('[data-cm-tag="' + gid + '"]');
        var meta = tagGroupMeta[gid] || { selection: 'single' };
        var match = tagPill.getAttribute('data-cm-match') || '';
        if (meta.resetting && match !== '') resetOtherGroups('tag:' + gid);
        if (meta.selection === 'multi') {
          if (match === '') {
            state.tagFilters[gid] = [];                 // "All" clears the group
          } else {
            var arr = state.tagFilters[gid];
            if (!Array.isArray(arr)) arr = state.tagFilters[gid] = [];
            var idx = arr.indexOf(match);
            if (idx === -1) arr.push(match); else arr.splice(idx, 1);   // toggle
          }
          updateMultiPills(group, gid);
        } else {
          setActive(group, tagPill);                    // single = radio
          state.tagFilters[gid] = match;
        }
        render(); return;
      }
      var portalEnter = e.target.closest('[data-cm-portal-enter]');
      if (portalEnter) {
        var pWrap = portalEnter.closest('[data-cm-portal]');
        if (pWrap) enterPortal(pWrap.getAttribute('data-cm-portal'));
        return;
      }
      if (e.target.closest('[data-cm-portal-exit]')) { exitPortal(); return; }
      var densityBtn = e.target.closest('[data-cm-density]');
      if (densityBtn) {
        var cols = densityBtn.getAttribute('data-cols');
        root.style.setProperty('--cm-cols', cols);
        setActive(root.querySelectorAll('[data-cm-density]'), densityBtn);
        return;
      }
      if (e.target.closest('[data-cm-clear-all]')) { clearAll(); return; }
      var rowClear = e.target.closest('[data-cm-row-clear]');
      if (rowClear) { clearRow(rowClear.closest('.collection-monolith__filter-row')); return; }
      var clearBtn = e.target.closest('[data-cm-clear]');
      if (clearBtn) { clearAll(); return; }
      var sbToggle = e.target.closest('[data-cm-sidebar-toggle]');
      if (sbToggle) {
        var sb = root.querySelector('[data-cm-sidebar]');
        if (sb) { var open = sb.classList.toggle('is-open'); sbToggle.setAttribute('aria-expanded', open ? 'true' : 'false'); }
        return;
      }
      var qDec = e.target.closest('[data-cm-qty-dec]');
      if (qDec) { stepQty(qDec, -1); return; }
      var qInc = e.target.closest('[data-cm-qty-inc]');
      if (qInc) { stepQty(qInc, 1); return; }
      // Whole card opens the product — but never hijack links, the action row, or form controls.
      var card = e.target.closest('[data-cm-item]');
      if (card && !e.target.closest('a, button, input, select, textarea, label, .collection-monolith__actions, .nm-portal, [data-no-nav]')) {
        var href = card.getAttribute('data-url');
        if (href) {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) window.open(href, '_blank', 'noopener');
          else window.location.assign(href);
        }
      }
    });

    function stepQty(btn, delta) {
      var wrap = btn.closest('[data-cm-qty]');
      var input = wrap && wrap.querySelector('.collection-monolith__qty-num');
      if (!input) return;
      var n = parseInt(input.value, 10);
      if (isNaN(n)) n = 1;
      n = Math.max(1, n + delta);
      input.value = n;
    }

    if (searchEl) {
      var t;
      searchEl.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          state.search = searchEl.value.trim().toLowerCase();
          render();
        }, 150);
      });
    }
    if (sortEl) {
      sortEl.value = state.sort;
      sortEl.addEventListener('change', function () { state.sort = sortEl.value; render(); });
    }

    /* ---------- per-row clear + "clear all" affordances ----------
     * A row's Clear only exists while that row has a selection — a permanent
     * Clear on every row is just noise. The buttons are injected here rather
     * than in Liquid so every row type (brand / stock / discount / tag / custom)
     * gets identical behaviour from one place. */
    function rowSelection(row) {
      if (!row || row.classList.contains('collection-monolith__portal')) return null;
      if (row.hasAttribute('data-cm-brand-row')) return state.brand !== '__all__' ? 'brand' : null;
      if (row.hasAttribute('data-cm-stock-row')) return state.stock !== 'all' ? 'stock' : null;
      if (row.hasAttribute('data-cm-discount-row')) return state.discount != null ? 'discount' : null;
      var gid = row.getAttribute('data-cm-tag-group');
      if (gid != null) {
        var v = state.tagFilters[gid];
        return (Array.isArray(v) ? v.length : !!v) ? 'tag:' + gid : null;
      }
      var key = row.getAttribute('data-cm-custom-group');
      if (key != null) {
        var c = state.custom[key];
        return (Array.isArray(c) ? c.length : !!c) ? 'custom:' + key : null;
      }
      return null;
    }

    function clearRow(row) {
      var which = rowSelection(row);
      if (!which) return;
      if (which === 'brand') { state.brand = '__all__'; reset(root.querySelectorAll('[data-cm-brand]'), '__all__', 'data-value'); }
      else if (which === 'stock') { state.stock = 'all'; reset(root.querySelectorAll('[data-cm-stock]'), 'all', 'data-value'); }
      else if (which === 'discount') { state.discount = null; reset(root.querySelectorAll('[data-cm-discount]'), '__all__', 'data-value'); }
      else if (which.indexOf('tag:') === 0) {
        var gid = which.slice(4), pills = root.querySelectorAll('[data-cm-tag="' + gid + '"]');
        if ((tagGroupMeta[gid] || {}).selection === 'multi') { state.tagFilters[gid] = []; updateMultiPills(pills, gid); }
        else { state.tagFilters[gid] = ''; reset(pills, '', 'data-cm-match'); }
      } else if (which.indexOf('custom:') === 0) {
        var key = which.slice(7), cp = root.querySelectorAll('[data-cm-custom="' + key + '"]');
        if ((customGroups[key] || {}).selection === 'multi') { state.custom[key] = []; updateCustomMultiPills(cp, key); }
        else { state.custom[key] = ''; reset(cp, '', 'data-cm-value'); }
      }
      render();
    }

    function ensureRowClears() {
      root.querySelectorAll('.collection-monolith__filter-row').forEach(function (row) {
        if (row.classList.contains('collection-monolith__portal')) return;   // not a filter
        if (row.querySelector('[data-cm-row-clear]')) return;
        var label = row.querySelector('.collection-monolith__filter-label');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'collection-monolith__row-clear';
        btn.setAttribute('data-cm-row-clear', '');
        btn.textContent = 'Clear';
        btn.hidden = true;

        /* Clear lives on its own line ABOVE the pills, sharing the header line
         * with the label and pinned right. It used to be floated inside the
         * pill flow, which shunted the pills sideways the moment it appeared.
         * With no label the head line still exists, so the button sits right —
         * and the pills below never move. */
        var head = document.createElement('div');
        head.className = 'collection-monolith__row-head';
        if (label) head.appendChild(label);          // move it, don't clone
        head.appendChild(btn);
        row.insertBefore(head, row.firstChild);
      });
    }

    function paintClearAffordances() {
      var anyActive = false;
      root.querySelectorAll('.collection-monolith__filter-row').forEach(function (row) {
        var on = !!rowSelection(row);
        if (on) anyActive = true;
        var btn = row.querySelector('[data-cm-row-clear]');
        if (btn) btn.hidden = !on;
      });
      // Search counts (clearAll resets it). Sort deliberately does NOT: it is an
      // ordering, not a filter, and clearAll leaves it alone — counting it here
      // produced a Clear all button that appeared after sorting and then did
      // nothing when clicked.
      if (!anyActive) anyActive = !!state.search;
      root.querySelectorAll('[data-cm-clear-all]').forEach(function (b) { b.hidden = !anyActive; });
    }

    /* ---------- dropdown rows ----------
     * A row set to "dropdown" keeps its pills in the DOM (hidden) and gets a
     * <select> mirroring them; choosing an option clicks the matching pill, so
     * every selection rule — single/multi, resetting, dynamic hiding, featured —
     * keeps working untouched. Dropdowns are single-choice by design. */
    function syncDropdowns() {
      root.querySelectorAll('.collection-monolith__filter-row[data-cm-pill-display="dropdown"]').forEach(function (row) {
        var box = row.querySelector('.collection-monolith__pills') || row;
        var pills = Array.prototype.slice.call(row.querySelectorAll('.collection-monolith__pill'));
        if (!pills.length) return;
        var sel = row.querySelector('[data-cm-row-select]');
        if (!sel) {
          sel = document.createElement('select');
          sel.className = 'collection-monolith__select';
          sel.setAttribute('data-cm-row-select', '');
          var lbl = row.querySelector('.collection-monolith__filter-label');
          sel.setAttribute('aria-label', lbl ? lbl.textContent.trim() : 'Filter');
          sel.addEventListener('change', function () {
            // re-query: rows like the discount row rebuild their innerHTML as
            // the catalogue streams in, so a captured node list goes stale.
            var live = row.querySelectorAll('.collection-monolith__pill');
            var target = live[parseInt(sel.value, 10)];
            if (target) target.click();
          });
          box.insertAdjacentElement('afterend', sel);
        }

        /* Walk the row's children IN ORDER so the ## headings and --- dividers
         * authored in an exact tag list survive the dropdown: a heading becomes
         * an <optgroup> and a divider a disabled separator. Reading only the
         * pills (as this did) silently dropped all of that structure. */
        var parts = [], sig = [], pillIndex = 0, group = null;
        Array.prototype.forEach.call(box.children, function (node) {
          if (node.classList.contains('collection-monolith__pill')) {
            var i = pillIndex++;
            if (node.hidden) return;
            parts.push({ kind: 'opt', i: i, text: node.textContent.trim(), on: node.classList.contains('is-active') });
            sig.push('o' + i + ':' + node.textContent.trim());
          } else if (node.classList.contains('collection-monolith__pill-heading')) {
            parts.push({ kind: 'group', text: node.textContent.trim() });
            sig.push('g:' + node.textContent.trim());
          } else if (node.classList.contains('collection-monolith__pill-divider')) {
            parts.push({ kind: 'rule' });
            sig.push('r');
          }
        });

        var signature = sig.join('|');
        if (sel.getAttribute('data-sig') !== signature) {
          sel.setAttribute('data-sig', signature);
          var html = '', open = false;
          parts.forEach(function (pt) {
            if (pt.kind === 'group') {
              if (open) html += '</optgroup>';
              html += '<optgroup label="' + escapeHtml(pt.text) + '">';
              open = true;
            } else if (pt.kind === 'rule') {
              html += '<option disabled>──────────</option>';
            } else {
              html += '<option value="' + pt.i + '">' + escapeHtml(pt.text) + '</option>';
            }
          });
          if (open) html += '</optgroup>';
          sel.innerHTML = html;
        }
        var active = parts.filter(function (pt) { return pt.kind === 'opt' && pt.on; })[0];
        var first = parts.filter(function (pt) { return pt.kind === 'opt'; })[0];
        sel.value = active ? String(active.i) : (first ? String(first.i) : '');
      });
    }

    function clearAll() {
      state.search = '';
      state.brand = '__all__';
      state.stock = 'all';
      state.discount = null;
      state.tagFilters = {};
      // Custom rows reset to their own "All" too. Portal mode is deliberately
      // NOT exited here: "clear filters" clears filters, it doesn't navigate.
      Object.keys(state.custom).forEach(function (k) {
        var pills = root.querySelectorAll('[data-cm-custom="' + k + '"]');
        if ((customGroups[k] || {}).selection === 'multi') { state.custom[k] = []; updateCustomMultiPills(pills, k); }
        else { state.custom[k] = ''; reset(pills, '', 'data-cm-value'); }
      });
      if (searchEl) searchEl.value = '';
      // reset pill active states to the "All" pill of each group
      reset(root.querySelectorAll('[data-cm-brand]'), '__all__', 'data-value');
      reset(root.querySelectorAll('[data-cm-stock]'), 'all', 'data-value');
      reset(root.querySelectorAll('[data-cm-discount]'), '__all__', 'data-value');
      root.querySelectorAll('[data-cm-tag-row]').forEach(function (row) {
        var gid = row.getAttribute('data-cm-tag-group');
        var meta = gid != null ? tagGroupMeta[gid] : null;
        var pills = row.querySelectorAll('[data-cm-tag]');
        if (meta && meta.selection === 'multi') {
          if (gid != null) state.tagFilters[gid] = [];
          updateMultiPills(pills, gid);
        } else {
          reset(pills, '', 'data-cm-match');
          if (gid != null) state.tagFilters[gid] = '';
        }
      });
      render();
    }
    function reset(pills, allValue, attr) {
      for (var i = 0; i < pills.length; i++) {
        var on = (pills[i].getAttribute(attr) || '') === allValue;
        pills[i].classList.toggle('is-active', on);
        pills[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }

    /* ---------- shareable URL: active filter state <-> query string ----------
     * Every non-default filter is serialised to a query param so a filtered view
     * can be copied/shared and reopened in the same state. Params: q (search),
     * brand, stock, disc (discount bucket), sort, and t_<gid> per tag group
     * (comma-joined for multi-select). writeUrl() runs at the end of render()
     * and short-circuits when the serialised state is unchanged, so the many
     * prefetch-driven re-renders never spam history. */
    var defaultSortVal = config.defaultSort || 'featured';
    /* We own exactly these keys. EVERYTHING else in the query string belongs to
     * someone else and must survive untouched: utm_* (paid-ad attribution),
     * page, variant, and Locksmith's ?key= grant — rewriting the URL to a bare
     * pathname on first paint would silently drop a shopper's ad attribution
     * and, worse, de-authorise them from a legitimate access link. */
    var OWNED_PREFIXES = /^(t_|c_)/;
    var OWNED_KEYS = ['q', 'brand', 'stock', 'disc', 'sort', 'portal'];
    function serializeState() {
      var p = new URLSearchParams(window.location.search);
      OWNED_KEYS.forEach(function (k) { p.delete(k); });
      // collect first: deleting while iterating a live key list is undefined
      var toDrop = [];
      p.forEach(function (_v, k) { if (OWNED_PREFIXES.test(k)) toDrop.push(k); });
      toDrop.forEach(function (k) { p.delete(k); });
      if (state.search) p.set('q', state.search);
      if (state.brand && state.brand !== '__all__') p.set('brand', state.brand);
      if (state.stock && state.stock !== 'all') p.set('stock', state.stock);
      if (state.discount != null) p.set('disc', String(state.discount));
      if (state.sort && state.sort !== defaultSortVal) p.set('sort', state.sort);
      Object.keys(state.tagFilters).forEach(function (gid) {
        var v = state.tagFilters[gid];
        if (Array.isArray(v)) { if (v.length) p.set('t_' + gid, v.join(',')); }
        else if (v) p.set('t_' + gid, v);
      });
      Object.keys(state.custom).forEach(function (key) {
        var v = state.custom[key];
        if (Array.isArray(v)) { if (v.length) p.set('c_' + key, v.join(',')); }
        else if (v) p.set('c_' + key, v);
      });
      if (state.portal) p.set('portal', state.portal);
      return p.toString();
    }
    var lastUrlQuery = null;
    function writeUrl() {
      if (typeof URLSearchParams === 'undefined' || !window.history || !history.replaceState) return;
      var qs = serializeState();
      if (qs === lastUrlQuery) return;      // no change since last write (e.g. prefetch re-render)
      lastUrlQuery = qs;
      var url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
      try { history.replaceState(history.state, '', url); } catch (e) {}
    }
    function restoreFromUrl() {
      if (typeof URLSearchParams === 'undefined') return;
      var p = new URLSearchParams(window.location.search);
      if (!p.toString()) return;
      var q = p.get('q');
      if (q) { state.search = q.toLowerCase(); if (searchEl) searchEl.value = q; }
      var brand = p.get('brand');
      if (brand) { state.brand = brand; reset(root.querySelectorAll('[data-cm-brand]'), brand, 'data-value'); }
      var stock = p.get('stock');
      if (stock) { state.stock = stock; reset(root.querySelectorAll('[data-cm-stock]'), stock, 'data-value'); }
      var disc = p.get('disc');
      if (disc != null && disc !== '') {
        var dn = parseInt(disc, 10);
        if (!isNaN(dn)) { state.discount = dn; reset(root.querySelectorAll('[data-cm-discount]'), disc, 'data-value'); }
      }
      var sort = p.get('sort');
      if (sort) { state.sort = sort; if (sortEl) sortEl.value = sort; }
      Object.keys(tagGroupMeta).forEach(function (gid) {
        var raw = p.get('t_' + gid);
        if (raw == null) return;
        var pills = root.querySelectorAll('[data-cm-tag="' + gid + '"]');
        if (tagGroupMeta[gid].selection === 'multi') {
          state.tagFilters[gid] = raw.split(',').filter(Boolean);
          updateMultiPills(pills, gid);
        } else {
          state.tagFilters[gid] = raw;
          reset(pills, raw, 'data-cm-match');
        }
      });
      Object.keys(customGroups).forEach(function (key) {
        var raw = p.get('c_' + key);
        if (raw == null) return;
        var pills = root.querySelectorAll('[data-cm-custom="' + key + '"]');
        if (customGroups[key].selection === 'multi') {
          state.custom[key] = raw.split(',').filter(Boolean);
          updateCustomMultiPills(pills, key);
        } else {
          state.custom[key] = raw;
          reset(pills, raw, 'data-cm-value');
        }
      });
      // Portal is restored last and loads its products asynchronously, so a
      // shared portal link opens straight into that view.
      var portalId = p.get('portal');
      if (portalId && portals[portalId]) {
        state.portal = portalId;
        paintPortalImages();
        // Must mirror enterPortal: prunePills un-hides individual base-absent
        // PILLS, but only this un-hides the ROW. Without it a shared link like
        // ?portal=X&t_g=foo left an active filter inside a display:none row —
        // invisible, and unclearable except via the global Clear all.
        paintBaseAbsentPills();
        loadPortal(portalId, function () { prunePills(); render(); });
      }
    }

    /* ---------- money formatting (from shop money_format) ---------- */
    function withCommas(val, dp) {
      var p = (Number(val) || 0).toFixed(dp).split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return p.join('.');
    }
    function money(amount) {
      var fmt = config.moneyFormat || '${{amount}}';
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, key) {
        switch (key) {
          case 'amount_no_decimals':
          case 'amount_no_decimals_with_comma_separator': return withCommas(amount, 0);
          default: return withCommas(amount, 2);
        }
      });
    }

    /* ---------- JS card builder (mirrors collection-card.monolith.liquid) ---------- */
    function escapeHtml(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
    function buildCard(p) {
      var available = (p.variants || []).some(function (v) { return v.available; });
      var firstAvail = (p.variants || []).filter(function (v) { return v.available; })[0] || (p.variants || [])[0] || {};
      var tagsArr = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
      var isPreorder = preorderTags.some(function (t) { return tagsArr.indexOf(t) !== -1; });
      var stock = isPreorder ? 'pre-order' : (available ? 'in-stock' : 'out-of-stock');
      var createdTs = Math.floor(new Date(p.created_at).getTime() / 1000) || 0;
      var nowTs = Math.floor(Date.now() / 1000);
      var isNew = newSeconds > 0 && createdTs > 0 && (nowTs - createdTs) <= newSeconds;
      /* Price must match the Liquid card, which uses product.price /
       * product.compare_at_price — the MINIMUM across variants ("from" pricing),
       * not the first available variant. Using the first variant here made the
       * first 24 server-rendered cards disagree with everything below the fold
       * on both the visible price and the -N% badge, and made the price sort
       * inconsistent across the same grid. compare_at is the min of the variants
       * that actually HAVE one, so a partial sale still shows a sensible MSRP. */
      var priceList = (p.variants || []).map(function (v) { return parseFloat(v.price); })
        .filter(function (n) { return !isNaN(n); });
      var price = priceList.length ? Math.min.apply(null, priceList) : 0;
      var capList = (p.variants || []).map(function (v) { return parseFloat(v.compare_at_price); })
        .filter(function (n) { return !isNaN(n) && n > 0; });
      var compareAt = capList.length ? Math.min.apply(null, capList) : 0;
      var onSale = compareAt > price;
      var pct = onSale ? Math.round((compareAt - price) / compareAt * 100) : 0;
      var url = escapeHtml('/products/' + p.handle);   // merchant-controlled, but escape for consistency
      var single = (p.variants || []).length === 1;
      // products.json images are OBJECTS ({src,...}), not strings — pull .src
      var imgObj = p.featured_image || (p.images && p.images[0]) || null;
      var imgSrc = imgObj ? (typeof imgObj === 'string' ? imgObj : (imgObj.src || '')) : '';
      var imgUrl = imgSrc ? (imgSrc + (imgSrc.indexOf('?') > -1 ? '&' : '?') + 'width=600') : '';

      var imgHtml = imgUrl
        ? '<img class="collection-monolith__img" src="' + escapeHtml(imgUrl) + '" loading="lazy" alt="' + escapeHtml(p.title) + '" width="300" height="300">'
        : '<div class="collection-monolith__img collection-monolith__img--placeholder"></div>';

      // RUNES = price$ rounded up; XP = that × 10
      var priceUp = Math.ceil(price);
      var runes = priceUp, xp = priceUp * 10;
      var saleBadge = onSale ? '<span class="collection-monolith__sale">Sale</span>' : '';
      var runesIconHtml = config.runesIcon ? '<img class="collection-monolith__pt-icon" src="' + config.runesIcon + '" alt="Runes" width="15" height="15" loading="lazy">' : '';
      var pointsBadge = runes > 0
        ? '<span class="collection-monolith__points">' +
            '<span class="collection-monolith__pt-line"><span class="collection-monolith__pt-num">+' + runes + '</span>' + runesIconHtml + '</span>' +
            '<span class="collection-monolith__pt-line"><span class="collection-monolith__pt-num">+' + xp + '</span><span class="collection-monolith__xp">XP</span></span>' +
          '</span>'
        : '';

      var vendorHtml = (config.showVendor && p.vendor) ? '<div class="collection-monolith__vendor">' + escapeHtml(p.vendor) + '</div>' : '';
      var newTag = isNew ? '<span class="collection-monolith__tag collection-monolith__tag--new">New</span>' : '';

      var stockHtml = stock === 'pre-order'
        ? '<span class="collection-monolith__stock collection-monolith__stock--preorder">Pre-Order</span>'
        : (stock === 'in-stock'
          ? '<span class="collection-monolith__stock collection-monolith__stock--instock">In Stock</span>'
          : '<span class="collection-monolith__stock collection-monolith__stock--sold">Sold Out</span>');

      var priceHtml =
        '<div class="collection-monolith__price">' +
          '<span class="collection-monolith__price-now">' + money(price) + '</span>' +
          (onSale ? '<s class="collection-monolith__price-msrp">MSRP ' + money(compareAt) + '</s>' : '') +
        '</div>' +
        '<div class="collection-monolith__price-meta">' +
          (onSale ? '<span class="collection-monolith__discount">-' + pct + '%</span>' : '') +
          stockHtml +
        '</div>';

      var actions;
      // Pre-order outranks availability: a pre-order product enters the action
      // branch even when out of stock (bought via oversell add-to-cart), so its
      // red PRE-ORDER button + notice always show. Only a NON-pre-order, sold-out
      // product falls through to the Notify Me trigger. firstAvail falls back to
      // variants[0], so the ATC form has a valid id even for an OOS pre-order.
      if (available || isPreorder) {
        if (single && config.enableAtc) {
          actions =
            '<form class="collection-monolith__form" action="/cart/add" method="post" enctype="multipart/form-data" data-product-form data-product-handle="' + escapeHtml(p.handle) + '">' +
              '<input type="hidden" name="form_type" value="product">' +
              '<input type="hidden" name="utf8" value="✓">' +
              '<input type="hidden" name="id" value="' + escapeHtml(firstAvail.id) + '">' +
              '<div class="collection-monolith__qty" data-cm-qty>' +
                '<button type="button" class="collection-monolith__qty-btn" data-cm-qty-dec aria-label="Decrease quantity" tabindex="-1">−</button>' +
                '<input class="collection-monolith__qty-num" type="text" name="quantity" value="1" inputmode="numeric" pattern="[0-9]*" aria-label="Quantity">' +
                '<button type="button" class="collection-monolith__qty-btn" data-cm-qty-inc aria-label="Increase quantity" tabindex="-1">+</button>' +
              '</div>' +
              (isPreorder
                ? '<button type="submit" class="Sd_addProduct collection-monolith__btn collection-monolith__btn--add collection-monolith__btn--preorder" data-cm-preorder aria-label="Pre-order this product">PRE-ORDER</button>'
                : '<button type="submit" class="Sd_addProduct collection-monolith__btn collection-monolith__btn--add" aria-label="' + escapeHtml(atcText) + '">' +
                    '<span class="collection-monolith__btn-label--d">' + escapeHtml(atcText) + '</span>' +
                    '<span class="collection-monolith__btn-label--m">' + escapeHtml(atcTextMobile) + '</span>' +
                  '</button>') +
            '</form>';
        } else if (config.enableQuickview) {
          actions = '<a class="collection-monolith__btn collection-monolith__btn--add quickView" href="' + url + '" data-href="' + url + '" data-cm-options>Choose options</a>';
        } else {
          actions = '<a class="collection-monolith__btn collection-monolith__btn--add" href="' + url + '">View product</a>';
        }
      } else {
        // Sold out (non-pre-order) → Notify Me trigger. The notify-me-preorder
        // SDK activates any element with id="restock-<handle>".
        actions = '<button type="button" id="restock-' + escapeHtml(p.handle) + '" class="collection-monolith__btn collection-monolith__btn--notify" data-no-nav>Notify me</button>';
      }

      var el = document.createElement('div');
      el.className = 'collection-monolith__item';
      el.setAttribute('data-cm-item', '');
      el.setAttribute('data-product-id', p.id);
      el.setAttribute('data-name', String(p.title || '').toLowerCase());
      el.setAttribute('data-title', p.title || '');
      el.setAttribute('data-brand', p.vendor || '');
      el.setAttribute('data-type', p.product_type || '');
      el.setAttribute('data-stock', stock);
      el.setAttribute('data-is-new', isNew ? 'true' : 'false');
      el.setAttribute('data-price', price.toFixed(2));
      el.setAttribute('data-discount', pct);
      el.setAttribute('data-available', available ? 'true' : 'false');
      el.setAttribute('data-tags', tagsArr.join('|'));
      el.setAttribute('data-created', createdTs);
      el.setAttribute('data-url', url);
      el.innerHTML =
        '<div class="collection-monolith__card">' +
          '<a class="collection-monolith__media" href="' + url + '" aria-label="' + escapeHtml(p.title) + '">' + imgHtml + saleBadge + pointsBadge + '</a>' +
          '<div class="collection-monolith__info">' +
            '<div class="collection-monolith__info-head">' +
              '<div class="collection-monolith__info-titles">' + vendorHtml +
                '<a class="collection-monolith__title" href="' + url + '">' + escapeHtml(p.title) + '</a>' +
              '</div>' + newTag +
            '</div>' +
            '<div class="collection-monolith__price-row">' + priceHtml + '</div>' +
            '<div class="collection-monolith__actions">' + actions + '</div>' +
          '</div>' +
        '</div>';
      return el;
    }

    /* ---------- dynamic pill availability (per group, over the WHOLE set) -----
     * For each group flagged `dynamic`, hide any pill whose tag has NO product
     * among those passing every OTHER active filter (matchesExcept skips the
     * group's own selection, so you can still switch within it — standard facet
     * behaviour). "All" is always visible. If the current selection becomes
     * unreachable, reset the group to "All" so results never strand at zero.
     *
     * WHOLE-CATALOGUE rule: only APPLY hiding once catalogueComplete — while the
     * prefetch streams, page-N-so-far is a partial set, so we leave every pill
     * visible and tighten when finish() runs. Prefetch off ⇒ we can only ever
     * see page 1, so we DON'T filter against it: warn once, leave the group's
     * pills to the static prunePills() behaviour (which, with prefetch off,
     * never runs — i.e. all pills stay). No silent wrong facets.
     * -------------------------------------------------------------------- */
    function applyDynamicPills() {
      var groups = [];
      for (var gid in tagGroupMeta) {
        if (!tagGroupMeta[gid].dynamic) continue;
        // A "resetting" row is a top-level switch and must stay wholly reachable,
        // so it opts out of narrowing even when Dynamic is also ticked.
        if (tagGroupMeta[gid].resetting) continue;
        if (!prefetchOn) {
          if (!dynamicWarned[gid]) {
            dynamicWarned[gid] = true;
            console.warn('[collection.monolith] dynamic tag group ' + gid + ' needs prefetch to see the whole catalogue — leaving all its pills (no partial-set filtering).');
          }
          continue;
        }
        if (!catalogueComplete) continue;        // still streaming: leave pills alone

        var avail = {};
        for (var i = 0; i < items.length; i++) {
          if (!matchesExcept(items[i], gid)) continue;
          var tags = data(items[i]).tags;
          for (var t = 0; t < tags.length; t++) avail[tags[t]] = true;
        }
        groups.push({ gid: gid, meta: tagGroupMeta[gid], avail: avail, pills: root.querySelectorAll('[data-cm-tag="' + gid + '"]') });
      }

      // Reset stranded selections ONE GROUP AT A TIME, then re-render so the
      // remaining groups are re-evaluated against the freed-up set. Resetting a
      // single group only LOOSENS its constraint (avail can only grow), so a
      // group stranded solely by another's now-cleared pick is spared instead of
      // being cleared alongside it. (Multi stranded values contribute no
      // products, so dropping them never strands another group — but the same
      // one-at-a-time path keeps the logic uniform and convergent.)
      for (var g = 0; g < groups.length; g++) {
        var G = groups[g], sel = state.tagFilters[G.gid];
        if (G.meta.selection === 'multi' && Array.isArray(sel)) {
          var kept = [];
          for (var s = 0; s < sel.length; s++) { if (G.avail[sel[s]]) kept.push(sel[s]); }
          if (kept.length !== sel.length) { state.tagFilters[G.gid] = kept; updateMultiPills(G.pills, G.gid); render(); return; }
        } else if (typeof sel === 'string' && sel && !G.avail[sel]) {
          state.tagFilters[G.gid] = '';
          reset(G.pills, '', 'data-cm-match');
          render(); return;
        }
      }

      // Hide unreachable pills; "All" (empty match) always stays.
      for (var g2 = 0; g2 < groups.length; g2++) {
        var pills = groups[g2].pills, av = groups[g2].avail;
        for (var p = 0; p < pills.length; p++) {
          var m = pills[p].getAttribute('data-cm-match') || '';
          pills[p].hidden = m !== '' && !av[m];
        }
      }

      applyDynamicCustom();
      applyDynamicBrand();
      applyDynamicDiscount();
    }

    /* Custom rows narrow the same way: a pill hides when no product passing the
     * OTHER filters satisfies its rule. Resetting rows opt out, as above. */
    function applyDynamicCustom() {
      if (!prefetchOn || !catalogueComplete) return;
      Object.keys(customGroups).forEach(function (key) {
        var meta = customGroups[key];
        if (!meta.dynamic || meta.resetting) return;
        var pool = [];
        for (var i = 0; i < items.length; i++) if (matches(items[i], null, key)) pool.push(data(items[i]));
        var reachable = {};
        meta.pills.forEach(function (gid) {
          var rule = customPills[gid];
          for (var j = 0; j < pool.length; j++) { if (customPillMatches(rule, pool[j])) { reachable[gid] = true; break; } }
        });
        var pills = root.querySelectorAll('[data-cm-custom="' + key + '"]');
        var sel = state.custom[key];
        if (Array.isArray(sel)) {
          var kept = sel.filter(function (v) { return reachable[v]; });
          if (kept.length !== sel.length) { state.custom[key] = kept; updateCustomMultiPills(pills, key); }
        } else if (sel && !reachable[sel]) {
          state.custom[key] = ''; reset(pills, '', 'data-cm-value');
        }
        for (var p = 0; p < pills.length; p++) {
          var v = pills[p].getAttribute('data-cm-value') || '';
          pills[p].hidden = v !== '' && !reachable[v];
        }
      });
    }

    /* Discount row, same rule as brand: hide a bucket when nothing passing the
     * OTHER filters falls inside it. The row already rebuilds its buckets from
     * whatever is on sale, but that is independent of the other filters — this
     * is what makes it react to them. */
    function applyDynamicDiscount() {
      if (!discountGroup.dynamic || discountGroup.resetting) return;
      if (!prefetchOn || !catalogueComplete) return;
      var savedDisc = state.discount;
      state.discount = null;                        // ignore our own selection
      var reachable = {};
      for (var i = 0; i < items.length; i++) {
        if (!matches(items[i])) continue;
        var d = data(items[i]);
        if (d.discount <= 0) continue;
        for (var k = 0; k < discountBuckets.length; k++) {
          var b = discountBuckets[k];
          if (d.discount >= b.min && d.discount <= b.max) { reachable[b.label] = true; break; }
        }
      }
      state.discount = savedDisc;
      var pills = root.querySelectorAll('[data-cm-discount]');
      if (state.discount != null && !reachable[state.discount]) {
        state.discount = null;
        reset(pills, '__all__', 'data-value');
      }
      for (var p2 = 0; p2 < pills.length; p2++) {
        var v = pills[p2].getAttribute('data-value') || '';
        pills[p2].hidden = v !== '__all__' && !reachable[parseInt(v, 10)];
      }
    }

    /* Brand row, same rule — hide vendors with nothing left under the others. */
    function applyDynamicBrand() {
      if (!brandGroup.dynamic || brandGroup.resetting) return;
      if (!prefetchOn || !catalogueComplete) return;
      var savedBrand = state.brand;
      state.brand = '__all__';                      // ignore our own selection
      var avail = {};
      for (var i = 0; i < items.length; i++) if (matches(items[i])) avail[data(items[i]).brand] = true;
      state.brand = savedBrand;
      var pills = root.querySelectorAll('[data-cm-brand]');
      if (state.brand !== '__all__' && !avail[state.brand]) {
        state.brand = '__all__'; reset(pills, '__all__', 'data-value');
      }
      for (var p = 0; p < pills.length; p++) {
        var v = pills[p].getAttribute('data-value') || '';
        pills[p].hidden = v !== '__all__' && !avail[v];
      }
    }

    /* ---------- featured pills ----------
     * Brand and custom rows are hoisted in Liquid (they're server-rendered once).
     * Tag rows can't be: the pill list is built from live collection tags across
     * two match modes, so we move the matching node to the front here instead —
     * moving it means it can never appear twice. */
    function hoistFeaturedTagPills() {
      Object.keys(tagGroupMeta).forEach(function (gid) {
        var want = tagGroupMeta[gid].featured;
        if (!want) return;
        var pills = root.querySelectorAll('[data-cm-tag="' + gid + '"]');
        if (!pills.length) return;
        var wrap = pills[0].parentNode, allPill = null, target = null;
        for (var i = 0; i < pills.length; i++) {
          var m = (pills[i].getAttribute('data-cm-match') || '').toLowerCase();
          if (m === '') { allPill = pills[i]; continue; }
          // accept the bare value or the full prefixed tag
          if (m === want || m.split(':').pop().trim() === want) { target = pills[i]; break; }
        }
        if (!target) return;
        target.classList.add('collection-monolith__pill--featured');
        if (allPill && allPill.nextSibling !== target) wrap.insertBefore(target, allPill.nextSibling);
      });
    }

    /* ---------- prune empty pills after full load ---------- */
    function prunePills() {
      // Only the cards currently IN SCOPE count. Portal cards stay in the DOM
      // after leaving a portal, so pruning against raw `items` left brand/tag/
      // stock pills for portal-only products visible in the normal collection —
      // clicking one gave zero results.
      var scope = items.filter(inScope);
      // brand pills
      root.querySelectorAll('[data-cm-brand]').forEach(function (pill) {
        var v = pill.getAttribute('data-value');
        if (v === '__all__') return;
        var any = scope.some(function (n) { return data(n).brand === v; });
        pill.hidden = !any;
      });
      // tag pills
      root.querySelectorAll('[data-cm-tag]').forEach(function (pill) {
        var m = pill.getAttribute('data-cm-match');
        if (!m) return;
        var any = scope.some(function (n) { return data(n).tags.indexOf(m) !== -1; });
        pill.hidden = !any;
      });
      /* custom pills — these were missing entirely, which is why a Custom filter
       * row stayed fully visible inside a portal even when not one of its pills
       * matched anything in the grid. Each pill is evaluated with the same rule
       * engine the filtering uses, against the in-scope products only. */
      root.querySelectorAll('[data-cm-custom]').forEach(function (pill) {
        var v = pill.getAttribute('data-cm-value') || '';
        if (!v) return;                                   // the row's own "All"
        var rule = customPills[v];
        if (!rule) return;
        var any = scope.some(function (n) { return customPillMatches(rule, data(n)); });
        pill.hidden = !any;
      });
      // stock pills (preorder / new)
      var hasPre = scope.some(function (n) { return data(n).stock === 'pre-order'; });
      var hasNew = scope.some(function (n) { return data(n).isNew; });
      root.querySelectorAll('[data-cm-stock][data-value="pre-order"]').forEach(function (p) { p.hidden = !hasPre; });
      root.querySelectorAll('[data-cm-stock][data-value="new"]').forEach(function (p) { p.hidden = !hasNew; });
    }

    /* ---------- background prefetch of the whole collection ---------- */
    function appendPage(products) {
      if (aborted) return;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < products.length; i++) {
        var p = products[i];
        if (!p || seenIds[p.id]) continue;
        if (vendorBlocked(p.vendor)) { seenIds[p.id] = true; continue; }
        seenIds[p.id] = true;
        frag.appendChild(buildCard(p));
      }
      grid.appendChild(frag);
    }

    /* ---------- portals ----------
     * A portal swaps the grid for a product set that is NOT in this collection.
     * Its products are fetched once (collection handle, or the whole catalogue
     * when only vendor/tags are given), built with the same card builder, and
     * tagged data-cm-portal-item so matches() can gate them. Locksmith-blocked
     * vendors are dropped here exactly as they are in the normal prefetch. */
    var portalLoaded = {};

    function fetchAllProducts(handle, onDone) {
      var size = config.jsonPageSize || 250;
      var base = handle ? ('/collections/' + handle + '/products.json') : '/products.json';
      var out = [], pg = 1, MAX_PAGES = 20;
      function next() {
        if (aborted) return;
        fetch(base + '?limit=' + size + '&page=' + pg, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) {
            if (aborted) return;
            var prods = (j && j.products) || [];
            out = out.concat(prods);
            if (prods.length === size && pg < MAX_PAGES) { pg++; next(); }
            else onDone(out);
          })
          .catch(function () { onDone(out); });
      }
      next();
    }

    function ruleAccepts(def, p) {
      if (def.vendor && String(p.vendor || '').toLowerCase() !== def.vendor) return false;
      var want = String(def.tags || '').split(',').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
      if (want.length) {
        var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
        for (var i = 0; i < want.length; i++) { if (tags.indexOf(want[i]) !== -1) return true; }
        return false;
      }
      return true;
    }

    function loadPortal(pid, onReady) {
      if (portalLoaded[pid]) { onReady(); return; }
      var def = portals[pid];
      if (!def) { onReady(); return; }
      setLoadingUI(true, items.length);
      fetchAllProducts(def.collection, function (prods) {
        var frag = document.createDocumentFragment(), n = 0;
        for (var i = 0; i < prods.length; i++) {
          var p = prods[i];
          if (!p || !ruleAccepts(def, p)) continue;
          if (vendorBlocked(p.vendor)) continue;                          // Locksmith
          var card = buildCard(p);
          card.setAttribute('data-cm-portal-item', pid);
          frag.appendChild(card); n++;
        }
        grid.appendChild(frag);
        portalLoaded[pid] = true;
        refreshCache();
        setLoadingUI(false, items.length);
        log('portal ' + pid + ': ' + n + ' product(s) loaded');
        onReady();
      });
    }

    /* Custom pills that name a collection need that collection's product ids.
     * Fetched once per handle at boot (shared across pills), then re-rendered —
     * until it lands such a pill simply matches nothing rather than everything. */
    function loadCustomCollections() {
      var handles = {};
      Object.keys(customPills).forEach(function (gid) {
        var h = customPills[gid].collection;
        if (h) (handles[h] = handles[h] || []).push(gid);
      });
      Object.keys(handles).forEach(function (h) {
        fetchAllProducts(h, function (prods) {
          var idset = {};
          for (var i = 0; i < prods.length; i++) if (prods[i]) idset[String(prods[i].id)] = true;
          handles[h].forEach(function (gid) { customPills[gid].collectionIds = idset; });
          log('custom filter collection "' + h + '": ' + prods.length + ' id(s)');
          render();
        });
      });
    }

    /* Exact-tag pills for tags this collection doesn't have are rendered hidden
     * (so dead pills never show in the base state) — but a portal deliberately
     * surfaces products from OUTSIDE the collection, whose tags are not in
     * collection.all_tags. Reveal them while a portal owns the grid; prunePills
     * then narrows to what the portal's products actually carry. */
    function paintBaseAbsentPills() {
      var inPortal = !!state.portal;
      root.querySelectorAll('[data-cm-base-absent]').forEach(function (pill) { pill.hidden = !inPortal; });
      root.querySelectorAll('[data-cm-base-absent-row]').forEach(function (row) { row.hidden = !inPortal; });
    }

    /* ---------- row chrome ----------
     * Hiding individual pills is not enough. A row whose only surviving pill is
     * its own "All" is dead furniture — worst in a portal, where the grid is
     * repopulated from outside the collection and most rows match nothing. This
     * prunes what is left over once the pills have been narrowed:
     *   • a row with no selectable pill left hides entirely (label included)
     *   • an inline "## heading" hides when every pill under it is gone
     *   • a "---" divider hides unless it still separates two visible pills
     *   • standalone Header blocks hide while a portal owns the grid, since
     *     they label filters that are no longer there
     * Runs last, after prunePills() and the dynamic passes. */
    function isAllPill(pill) {
      if (pill.hasAttribute('data-cm-match')) return (pill.getAttribute('data-cm-match') || '') === '';
      if (pill.hasAttribute('data-cm-value')) return (pill.getAttribute('data-cm-value') || '') === '';
      var v = pill.getAttribute('data-value');
      return v === '__all__' || v === 'all';
    }

    function paintRowChrome() {
      root.querySelectorAll('.collection-monolith__filter-row').forEach(function (row) {
        if (row.classList.contains('collection-monolith__portal')) return;   // not a filter
        var box = row.querySelector('.collection-monolith__pills');
        if (!box) return;
        var pills = Array.prototype.slice.call(row.querySelectorAll('.collection-monolith__pill'));
        if (!pills.length) return;

        var realVisible = pills.filter(function (p) { return !p.hidden && !isAllPill(p); }).length;

        // inline headings: visible only if a pill under them survived
        var kids = Array.prototype.slice.call(box.children);
        var headings = [];
        var current = null;
        kids.forEach(function (node) {
          if (node.classList.contains('collection-monolith__pill-heading')) {
            current = { node: node, count: 0 };
            headings.push(current);
          } else if (node.classList.contains('collection-monolith__pill-divider')) {
            current = null;                                   // a divider ends the group
          } else if (node.classList.contains('collection-monolith__pill')) {
            if (current && !node.hidden && !isAllPill(node)) current.count++;
          }
        });
        headings.forEach(function (h) { h.node.hidden = h.count === 0; });

        // dividers: keep one only when it still sits between two visible pills
        kids.forEach(function (node, idx) {
          if (!node.classList.contains('collection-monolith__pill-divider')) return;
          var before = false, after = false;
          for (var i = idx - 1; i >= 0; i--) {
            var n = kids[i];
            if (n.classList.contains('collection-monolith__pill') && !n.hidden && !isAllPill(n)) { before = true; break; }
          }
          for (var j = idx + 1; j < kids.length; j++) {
            var m = kids[j];
            if (m.classList.contains('collection-monolith__pill') && !m.hidden && !isAllPill(m)) { after = true; break; }
          }
          node.hidden = !(before && after);
        });

        row.hidden = realVisible === 0;
      });

      // standalone Header blocks: nothing left to label while a portal is active
      root.querySelectorAll('.collection-monolith__heading-row').forEach(function (h) {
        h.hidden = !!state.portal;
      });
    }

    function paintPortalImages() {
      root.querySelectorAll('[data-cm-portal]').forEach(function (node) {
        var pid = node.getAttribute('data-cm-portal');
        var active = state.portal === pid;
        var enter = node.querySelector('[data-cm-portal-enter]');
        var exit = node.querySelector('[data-cm-portal-exit]');
        // An exit control ALWAYS exists (the Liquid falls back to a text button
        // when no Active image is set), so the cover can always be swapped out.
        // Never leave only the enter button visible while active — clicking it
        // would re-enter the portal and wipe the shopper's filters, trapping
        // them with no way back except a page reload.
        if (enter) enter.hidden = active;
        if (exit) exit.hidden = !active;
        node.classList.toggle('is-active', active);
      });
    }

    function enterPortal(pid) {
      if (!portals[pid]) return;
      state.portal = pid;
      // Loading UI goes up BEFORE the first render: clearAll() renders an empty
      // grid while the (sequential, multi-page) fetch runs, and without this the
      // shopper reads "No results — try a different search" for the whole wait.
      setLoadingUI(true, 0);
      clearAll();                 // filters are meaningless across a grid swap
      paintPortalImages();
      paintBaseAbsentPills();
      loadPortal(pid, function () { prunePills(); render(); focusPortal(pid); });
    }

    function exitPortal() {
      state.portal = null;
      clearAll();
      paintPortalImages();
      paintBaseAbsentPills();
      prunePills();
      render();
      focusPortal(null);
    }

    /* Entering/leaving a portal hides the very control that was activated, which
     * would drop focus to the document start. Move focus to whichever control is
     * visible now (the exit button on enter, the cover on exit). */
    function focusPortal(pid) {
      // A portal renders TWICE — sidebar copy and mobile strip — and CSS shows
      // one per breakpoint. querySelector always returned the sidebar copy, so
      // below 990px we were focusing a display:none node and focus fell to the
      // document body on every portal enter/exit. Pick the rendered one.
      var sel = pid ? '[data-cm-portal="' + pid + '"]' : '[data-cm-portal]';
      var nodes = Array.prototype.slice.call(root.querySelectorAll(sel));
      var node = nodes.filter(function (n) { return n.offsetParent !== null; })[0] || nodes[0];
      if (!node) return;
      var next = node.querySelector('[data-cm-portal-exit]:not([hidden]), [data-cm-portal-enter]:not([hidden])');
      if (next && typeof next.focus === 'function') next.focus();
    }

    function setLoadingUI(on, loadedCount) {
      loading = on;
      if (loadingEl) loadingEl.hidden = !on;
      if (loadedEl && loadedCount != null) loadedEl.textContent = loadedCount;
      if (totalEl) totalEl.textContent = config.productsCount;
    }

    function prefetch() {
      if (!config.prefetch || !config.handle) { log('prefetch disabled'); return; }
      var size = config.jsonPageSize || 250;
      var totalPages = Math.max(1, Math.ceil((config.productsCount || 0) / size));
      if (totalPages <= 1 && (config.productsCount || 0) <= items.length) {
        // Page 1 already IS the whole catalogue — prune empty pills and let the
        // dynamic groups filter, same as finish() does after a full prefetch.
        log('nothing to prefetch');
        catalogueComplete = true;
        prunePills();
        render();
        return;
      }

      /* Cache hit: rebuild from memory with no network at all — this is what makes
       * repeated theme-editor re-renders cheap. */
      var cached = PRODUCT_CACHE[config.handle];
      if (cached && Object.keys(cached).length) {
        Object.keys(cached).sort(function (a, b) { return a - b; })
          .forEach(function (k) { appendPage(cached[k]); });
        refreshCache();
        rebuildDiscountPills();
        catalogueComplete = true;
        prunePills();
        render();
        if (pagination) pagination.style.display = 'none';
        log('prefetch served from cache (no network)');
        return;
      }

      var initialCount = items.length;
      setLoadingUI(true, items.length);
      var base = '/collections/' + config.handle + '/products.json?limit=' + size;
      var page = 1, active = 0, done = false, errors = 0;
      var CONCURRENCY = 3;
      log('prefetch start: ' + totalPages + ' page(s) of ' + size + ' (count=' + config.productsCount + ')');

      function pump() {
        if (aborted) return;
        while (active < CONCURRENCY && !done && page <= totalPages) {
          fetchPage(page++);
        }
      }
      function fetchPage(pg) {
        active++;
        fetch(base + '&page=' + pg, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
          .then(function (r) {
            if (!r.ok) { errors++; console.error('[collection.monolith] page ' + pg + ' → HTTP ' + r.status); return { products: [] }; }
            return r.json();
          })
          .then(function (json) {
            if (aborted) { done = true; return; }
            var prods = (json && json.products) || [];
            if (prods.length) {
              var bucket = PRODUCT_CACHE[config.handle] || (PRODUCT_CACHE[config.handle] = {});
              bucket[pg] = prods;                       // survives a re-init
            }
            if (prods.length) { appendPage(prods); refreshCache(); rebuildDiscountPills(); render(); }
            if (prods.length < size) done = true; // last page reached
            setLoadingUI(true, items.length);
          })
          .catch(function (err) { errors++; console.error('[collection.monolith] page ' + pg + ' fetch failed', err); })
          .then(function () {
            active--;
            if (aborted) return;
            if (!done && page <= totalPages) pump();
            else if (active === 0) finish();
          });
      }
      function finish() {
        refreshCache();
        var added = items.length - initialCount;
        log('prefetch done: +' + added + ' products (' + items.length + ' total, ' + errors + ' error(s))');
        catalogueComplete = true;   // whole set is in — dynamic pills may now narrow
        prunePills();
        rebuildDiscountPills();
        // Only hide the server pagination if prefetch actually loaded the rest.
        // If it failed/added nothing, KEEP pagination so the catalogue stays browsable.
        if (pagination) {
          if (added > 0 && errors === 0) { pagination.style.display = 'none'; }
          else { pagination.style.display = ''; if (added === 0) console.warn('[collection.monolith] prefetch added 0 products — keeping pagination as fallback'); }
        }
        setLoadingUI(false, items.length);
        render();
      }
      pump();
    }

    /* ---------- restock "Notify me" app integration (.nm-portal) ---------- */
    function placeNotify(portal) {
      if (!portal || portal.getAttribute('data-cm-placed') === '1') return;
      var card = portal.closest('[data-cm-item]');
      if (!card) return;
      var actions = card.querySelector('.collection-monolith__actions');
      if (!actions) return;
      portal.setAttribute('data-cm-placed', '1');
      actions.appendChild(portal);            // move it into our action slot
      actions.classList.add('cm-has-notify'); // CSS then hides our ADD / Sold-out
    }
    function hookNotify() {
      var existing = root.querySelectorAll('.nm-portal');
      for (var i = 0; i < existing.length; i++) placeNotify(existing[i]);
      if (typeof MutationObserver === 'undefined') return;
      var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (!n || n.nodeType !== 1) continue;
            if (n.classList && n.classList.contains('nm-portal')) placeNotify(n);
            else if (n.querySelectorAll) {
              var inner = n.querySelectorAll('.nm-portal');
              for (var k = 0; k < inner.length; k++) placeNotify(inner[k]);
            }
          }
        }
      });
      mo.observe(grid, { childList: true, subtree: true });
      teardown.push(function () { mo.disconnect(); });
    }

    /* ---------- footer parallax (works on desktop + mobile) ---------- */
    function initParallax() {
      var imgs = Array.prototype.slice.call(root.querySelectorAll('.collection-monolith__footer--parallax .collection-monolith__footer-img'));
      if (!imgs.length) return;
      var strength = Number(config.parallaxStrength);
      if (isNaN(strength)) strength = 55;
      var raf = window.requestAnimationFrame || function (f) { return setTimeout(f, 16); };
      var ticking = false;
      function update() {
        ticking = false;
        var vh = window.innerHeight || document.documentElement.clientHeight;
        for (var i = 0; i < imgs.length; i++) {
          var item = imgs[i].parentNode;
          var r = item.getBoundingClientRect();
          if (r.bottom < -120 || r.top > vh + 120) continue;               // skip offscreen
          var denom = (vh / 2 + r.height / 2) || 1;
          var p = (r.top + r.height / 2 - vh / 2) / denom;                  // ~ -1 (below) .. 1 (above)
          if (p > 1) p = 1; else if (p < -1) p = -1;
          imgs[i].style.transform = 'translate3d(0,' + (-p * strength).toFixed(1) + 'px,0)';
        }
      }
      function onScroll() { if (!ticking) { ticking = true; raf(update); } }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      teardown.push(function () {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      });
      update();
    }

    /* ---------- go ---------- */
    refreshCache();
    items.forEach(function (n) { var id = n.getAttribute('data-product-id'); if (id) seenIds[id] = true; });
    if (sortEl) sortEl.value = state.sort;
    log('init: ' + items.length + ' server card(s), prefetch=' + config.prefetch + ', handle=' + config.handle + ', count=' + config.productsCount + ', blocked=' + JSON.stringify(blockedVendors));
    rebuildDiscountPills();
    ensureRowClears();         // inject the per-row Clear buttons once
    hoistFeaturedTagPills();   // featured pill leads its row before first paint
    loadCustomCollections();   // resolve any collection-rule pills in the background
    paintPortalImages();
    restoreFromUrl();      // seed state + pill UI from a shared/bookmarked URL before first paint
    render();
    /* In the editor, hold an UNCACHED first fetch briefly: dragging a range
     * slider fires many re-renders a second, and each one starting a catalogue
     * fetch is what makes the preview crawl. Teardown cancels it. */
    if (inDesignMode) {
      var delayedPrefetch = setTimeout(function () { if (!aborted) prefetch(); }, 1200);
      teardown.push(function () { clearTimeout(delayedPrefetch); });
    } else {
      prefetch();
    }
    hookNotify();
    initParallax();
  }

  /* ---------- pre-order add-to-cart confirmation (blocking) ----------
   * A single-variant pre-order card renders a red PRE-ORDER submit button
   * flagged data-cm-preorder. We intercept its click in the CAPTURE phase —
   * before the theme's jQuery (bubble-phase) cart handler — and require the
   * shopper to acknowledge a shipping notice first. On confirm we re-click the
   * same button; the interceptor then steps aside and the native add proceeds.
   * Bound once at document level so it covers server + prefetched cards alike.
   * The notice copy lives here (single source of truth); the card markup only
   * carries the flag. */
  var PREORDER_NOTICE = 'Please note that this is a pre-order product that will ship on or shortly after the official release date.';

  function preorderModal(onConfirm) {
    var overlay = document.createElement('div');
    overlay.className = 'collection-monolith__po-overlay';
    overlay.innerHTML =
      '<div class="collection-monolith__po-dialog" role="alertdialog" aria-modal="true" aria-labelledby="cm-po-title" aria-describedby="cm-po-msg">' +
        '<h2 id="cm-po-title" class="collection-monolith__po-title">Pre-order</h2>' +
        '<p id="cm-po-msg" class="collection-monolith__po-msg"></p>' +
        '<div class="collection-monolith__po-actions">' +
          '<button type="button" class="collection-monolith__po-btn collection-monolith__po-btn--cancel" data-cm-po-cancel>Cancel</button>' +
          '<button type="button" class="collection-monolith__po-btn collection-monolith__po-btn--ok" data-cm-po-ok>Add to cart</button>' +
        '</div>' +
      '</div>';
    overlay.querySelector('.collection-monolith__po-msg').textContent = PREORDER_NOTICE;

    // Remember who opened the dialog so focus can go back there on close —
    // otherwise dismissing it drops the caret to the document start.
    var opener = document.activeElement;

    function close() {
      document.removeEventListener('keydown', onKey, true);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    }
    /* Focus trap: it's a modal alertdialog, so Tab must cycle WITHIN it. Without
     * this, Tab walks straight out into the page behind while the dialog is
     * still blocking the add-to-cart it is asking about. */
    function focusables() {
      return Array.prototype.slice.call(overlay.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) { return !el.disabled && el.offsetParent !== null; });
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      // Focus outside the dialog (or wrapping off either end) comes back inside.
      if (!overlay.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target.closest('[data-cm-po-cancel]')) { close(); }
      else if (e.target.closest('[data-cm-po-ok]')) { close(); onConfirm(); }
    });
    document.addEventListener('keydown', onKey, true);
    document.body.appendChild(overlay);
    var ok = overlay.querySelector('[data-cm-po-ok]');
    if (ok) ok.focus();
  }

  if (!window.__cmPreorderConfirmBound) {
    window.__cmPreorderConfirmBound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-cm-preorder]') : null;
      if (!btn) return;
      // Second pass (post-confirm): stand aside so the native add fires.
      if (btn.getAttribute('data-cm-po-ok') === '1') { btn.removeAttribute('data-cm-po-ok'); return; }
      // First pass: block the native handler entirely, ask first.
      e.preventDefault();
      e.stopImmediatePropagation();
      preorderModal(function () {
        btn.setAttribute('data-cm-po-ok', '1');
        btn.click();
      });
    }, true);
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  // Theme editor: (re)initialize when a section is rendered, added, or reordered
  document.addEventListener('shopify:section:load', function (e) {
    var r = rootFrom(e.target);
    if (r && r.__cmTeardown) r.__cmTeardown();   // release a previous instance first
    initRoot(r);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var r = rootFrom(e.target);
    if (r && r.__cmTeardown) r.__cmTeardown();
  });
})();
